import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/layout/PageHero';
import { Card } from '../components/ui/Card';
import { SectionHeading } from '../components/ui/SectionHeading';
import { FlagIcon, ScrollTextIcon, LandmarkIcon, TargetIcon, ExternalLinkIcon, CheckCircle2Icon } from 'lucide-react';
const aims = ['Achieve greater unity and solidarity between African countries and their people', 'Defend the sovereignty, territorial integrity and independence of its Member States', 'Accelerate the political and socio-economic integration of the continent', 'Promote and defend African common positions on issues of interest to the continent and its peoples', 'Encourage international cooperation', 'Promote peace, security, and stability on the continent', 'Promote democratic principles and institutions, popular participation and good governance', "Promote and protect human and peoples' rights in accordance with the African Charter on Human and Peoples' Rights and other relevant human rights instruments", 'Establish the necessary conditions which enable the continent to play its rightful role in the global economy and in international negotiations', 'Promote sustainable development at the economic, social and cultural levels as well as the integration of African economies', 'Promote cooperation in all fields of human activity to raise the living standards of African peoples', 'Coordinate and harmonise the policies between the existing and future Regional Economic Communities for the gradual attainment of the objectives of the Union', 'Advance the development of the continent by promoting research in all fields, in particular in science and technology', 'Work with relevant international partners in the eradication of preventable diseases and the promotion of good health on the continent', 'Ensure the effective participation of women in decision-making, particularly in the political, economic and socio-cultural areas', 'Develop and promote common policies on trade, defence and foreign relations to ensure the defence of the Continent and the strengthening of its negotiating positions', 'Invite and encourage the full participation of the African Diaspora as an important part of our Continent, in the building of the African Union'];
const oauObjectives = ['To promote the unity and solidarity of the African States', 'To coordinate and intensify their cooperation and efforts to achieve a better life for the peoples of Africa', 'To defend their sovereignty, their territorial integrity and independence', 'To eradicate all forms of colonialism from Africa', 'To promote international cooperation, having due regard to the Charter of the United Nations and the Universal Declaration of Human Rights'];
const principalOrgans = ['The Assembly of Heads of State and Government', 'The Executive Council', 'The Permanent Representatives Committee (PRC)', 'Specialised Technical Committees (STCs)', 'The Peace and Security Council', 'The African Union Commission', 'The Pan-African Parliament', 'The Economic, Social & Cultural Council (ECOSOCC)'];
const judicialOrgans = ["African Commission on Human and Peoples' Rights (ACHPR)", "African Court on Human and Peoples' Rights (AfCHPR)", 'AU Commission on International Law (AUCIL)', 'AU Advisory Board on Corruption (AUABC)', 'African Committee of Experts on the Rights and Welfare of the Child'];
export function AboutAU() {
  return <div data-id="element-514">
      <PageHero title="About the African Union" subtitle="A continental body uniting 55 member states across Africa — driving integration, peace, and prosperity" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'About the African Union'
    }]} data-id="element-515" />

      {/* Intro */}
      <section className="py-16 bg-white" data-id="element-516">
        <div className="max-w-4xl mx-auto px-4" data-id="element-517">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-518">
            <p className="text-xl text-gray-700 leading-relaxed mb-6" data-id="element-519">
              The African Union (AU) is a continental body consisting of the{' '}
              <strong className="text-au-green" data-id="element-520">55 member states</strong> that
              make up the countries of the African Continent. It was officially
              launched in <strong data-id="element-521">2002</strong> as a successor to the
              Organisation of African Unity (OAU, 1963–1999).
            </p>

            <div className="grid grid-cols-3 gap-4 mt-8" data-id="element-522">
              <Card className="text-center bg-au-green text-white border-au-green" data-id="element-523">
                <div className="text-3xl font-bold mb-1" data-id="element-524">55</div>
                <div className="text-sm text-au-green-light" data-id="element-525">Member States</div>
              </Card>
              <Card className="text-center" data-id="element-526">
                <div className="text-3xl font-bold text-au-green mb-1" data-id="element-527">
                  1963
                </div>
                <div className="text-sm text-gray-600" data-id="element-528">OAU Founded</div>
              </Card>
              <Card className="text-center bg-au-green text-white border-au-green" data-id="element-529">
                <div className="text-3xl font-bold mb-1" data-id="element-530">2002</div>
                <div className="text-sm text-au-green-light" data-id="element-531">AU Launched</div>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* History */}
      <section className="py-16 bg-ivory" data-id="element-532">
        <div className="max-w-4xl mx-auto px-4" data-id="element-533">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-534">
            <div className="flex items-center gap-3 mb-4" data-id="element-535">
              <div className="w-12 h-12 bg-au-green rounded-lg flex items-center justify-center" data-id="element-536">
                <ScrollTextIcon size={24} className="text-white" data-id="element-537" />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal" data-id="element-538">
                History
              </h2>
            </div>
            <div className="w-16 h-1 bg-au-gold mb-8" data-id="element-539"></div>

            <div className="space-y-6 text-gray-700 leading-relaxed" data-id="element-540">
              <p data-id="element-541">
                In May 1963, 32 Heads of independent African States met in Addis
                Ababa, Ethiopia to sign the Charter creating Africa's first
                post-independence continental institution, The Organisation of
                African Unity (OAU). The OAU was the manifestation of the
                pan-African vision for an Africa that was united, free and in
                control of its own destiny.
              </p>
              <p data-id="element-542">
                This was solemnised in the OAU Charter in which the founding
                fathers recognised that freedom, equality, justice and dignity
                were essential objectives for the achievement of the legitimate
                aspirations of the African peoples and that there was a need to
                promote understanding among Africa's peoples and foster
                cooperation among African states in response to the aspirations
                of Africans for brotherhood and solidarity, in a larger unity
                transcending ethnic and national differences.
              </p>
              <p data-id="element-543">
                The guiding philosophy was that of{' '}
                <strong data-id="element-544">Pan-Africanism</strong> which centred on African
                socialism and promoted African unity, the communal
                characteristic and practices of African communities, and a drive
                to embrace Africa's culture and common heritage.
              </p>
            </div>

            <Card className="mt-8 bg-au-green-light border-au-green" data-id="element-545">
              <h3 className="font-bold text-charcoal mb-4 flex items-center gap-2" data-id="element-546">
                <FlagIcon size={20} className="text-au-green" data-id="element-547" />
                Main Objectives of the OAU
              </h3>
              <ul className="space-y-3" data-id="element-548">
                {oauObjectives.map((obj, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-549">
                    <CheckCircle2Icon size={18} className="text-au-green flex-shrink-0 mt-1" data-id="element-550" />
                    <span data-id="element-551">{obj}</span>
                  </li>)}
              </ul>
            </Card>

            <div className="space-y-6 text-gray-700 leading-relaxed mt-8" data-id="element-552">
              <p data-id="element-553">
                Through the OAU Coordinating Committee for the Liberation of
                Africa, the Continent worked and spoke as one with undivided
                determination in forging an international consensus in support
                of the liberation struggle and the fight against apartheid. The
                OAU provided an effective forum that enabled all Member States
                to adopt coordinated positions on matters of common concern to
                the continent in international fora and defend the interests of
                Africa effectively.
              </p>
              <p data-id="element-554">
                On <strong data-id="element-555">9 September 1999</strong>, the Heads of State and
                Government of the Organisation of African Unity (OAU) issued the{' '}
                <strong data-id="element-556">Sirte Declaration</strong> calling for the establishment
                of an African Union, with a view to accelerating the process of
                integration in the continent to enable Africa to play its
                rightful role in the global economy while addressing
                multifaceted social, economic and political problems compounded
                by certain negative aspects of globalisation.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Launch of the AU */}
      <section className="py-16 bg-white" data-id="element-557">
        <div className="max-w-4xl mx-auto px-4" data-id="element-558">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-559">
            <div className="flex items-center gap-3 mb-4" data-id="element-560">
              <div className="w-12 h-12 bg-au-green rounded-lg flex items-center justify-center" data-id="element-561">
                <FlagIcon size={24} className="text-white" data-id="element-562" />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal" data-id="element-563">
                The Launch of the African Union
              </h2>
            </div>
            <div className="w-16 h-1 bg-au-gold mb-8" data-id="element-564"></div>

            <div className="space-y-6 text-gray-700 leading-relaxed" data-id="element-565">
              <p data-id="element-566">
                The African Union (AU) was officially launched in{' '}
                <strong data-id="element-567">July 2002 in Durban, South Africa</strong>, following a
                decision in September 1999 by its predecessor, the OAU, to
                create a new continental organisation to build on its work.
              </p>
              <p data-id="element-568">
                The decision to re-launch Africa's pan-African organisation was
                the outcome of a consensus by African leaders that, in order to
                realise Africa's potential, there was a need to refocus
                attention from the fight for decolonisation and ridding the
                continent of apartheid — which had been the focus of the OAU —
                towards increased cooperation and integration of African states
                to drive Africa's growth and economic development.
              </p>
            </div>

            <Card className="mt-8 bg-au-green text-white border-au-green" data-id="element-569">
              <div className="flex items-start gap-4" data-id="element-570">
                <TargetIcon size={32} className="text-au-gold flex-shrink-0" data-id="element-571" />
                <div data-id="element-572">
                  <h3 className="font-serif text-xl mb-2" data-id="element-573">Our Vision</h3>
                  <p className="text-au-green-light italic text-lg leading-relaxed" data-id="element-574">
                    "An Integrated, Prosperous and Peaceful Africa, driven by
                    its own citizens and representing a dynamic force in the
                    global arena."
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* AU Aims */}
      <section className="py-16 bg-ivory" data-id="element-575">
        <div className="max-w-6xl mx-auto px-4" data-id="element-576">
          <SectionHeading title="Aims of the African Union" subtitle="As set out in the Constitutive Act of the African Union and the Protocol on Amendments to the Constitutive Act" centered data-id="element-577" />

          <div className="grid md:grid-cols-2 gap-4 mt-12" data-id="element-578">
            {aims.map((aim, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: i * 0.03
          }} data-id="element-579">
                <Card className="h-full flex items-start gap-3" data-id="element-580">
                  <div className="w-8 h-8 bg-au-green-light rounded-full flex items-center justify-center flex-shrink-0" data-id="element-581">
                    <span className="text-au-green font-bold text-sm" data-id="element-582">
                      {i + 1}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-583">{aim}</p>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Organs */}
      <section className="py-16 bg-white" data-id="element-584">
        <div className="max-w-6xl mx-auto px-4" data-id="element-585">
          <SectionHeading title="Structure & Organs of the AU" subtitle="The work of the AU is implemented through several principal decision-making organs" centered data-id="element-586" />

          <div className="grid md:grid-cols-2 gap-6 mt-12" data-id="element-587">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-588">
              <Card hover className="h-full" data-id="element-589">
                <div className="flex items-center gap-3 mb-4" data-id="element-590">
                  <div className="w-12 h-12 bg-au-green rounded-lg flex items-center justify-center" data-id="element-591">
                    <LandmarkIcon size={24} className="text-white" data-id="element-592" />
                  </div>
                  <h3 className="text-xl font-serif text-charcoal" data-id="element-593">
                    Principal Decision-Making Organs
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-594">
                  {principalOrgans.map((organ, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-595">
                      <CheckCircle2Icon size={18} className="text-au-green flex-shrink-0 mt-1" data-id="element-596" />
                      <span className="text-sm" data-id="element-597">{organ}</span>
                    </li>)}
                </ul>
              </Card>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-598">
              <Card hover className="h-full" data-id="element-599">
                <div className="flex items-center gap-3 mb-4" data-id="element-600">
                  <div className="w-12 h-12 bg-au-green rounded-lg flex items-center justify-center" data-id="element-601">
                    <ScrollTextIcon size={24} className="text-white" data-id="element-602" />
                  </div>
                  <h3 className="text-xl font-serif text-charcoal" data-id="element-603">
                    Judicial, Legal & Human Rights Organs
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-604">
                  {judicialOrgans.map((organ, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-605">
                      <CheckCircle2Icon size={18} className="text-au-green flex-shrink-0 mt-1" data-id="element-606" />
                      <span className="text-sm" data-id="element-607">{organ}</span>
                    </li>)}
                </ul>
              </Card>
            </motion.div>
          </div>

          <div className="mt-8 bg-au-green-light rounded-lg p-6" data-id="element-608">
            <p className="text-gray-700 leading-relaxed" data-id="element-609">
              The AU is also working towards the establishment of continental
              financial institutions — <strong data-id="element-610">The African Central Bank</strong>
              , <strong data-id="element-611">The African Investment Bank</strong>, and{' '}
              <strong data-id="element-612">the African Monetary Fund</strong>. The Regional Economic
              Communities (RECs) and the African Peer Review Mechanism are also
              key bodies that constitute the structure of the African Union.
            </p>
          </div>
        </div>
      </section>

      {/* Agenda 2063 */}
      <section className="py-16 bg-au-green text-white" data-id="element-613">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-614">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-615">
            <h2 className="text-3xl md:text-4xl font-serif mb-4" data-id="element-616">
              Agenda 2063
            </h2>
            <div className="w-24 h-1 bg-au-gold mx-auto mb-6" data-id="element-617"></div>
            <p className="text-lg text-au-green-light leading-relaxed mb-8" data-id="element-618">
              To ensure the realisation of its objectives and the attainment of
              the Pan-African vision of an integrated, prosperous and peaceful
              Africa, <strong className="text-white" data-id="element-619">Agenda 2063</strong> was
              developed as a strategic framework for Africa's long-term
              socio-economic and integrative transformation. Agenda 2063 calls
              for greater collaboration and support for African-led initiatives
              to ensure the achievement of the aspirations of African people.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}